import { useState } from 'react';
import type { FormEvent } from 'react';

const FORM_URL = 'https://readdy.ai/api/form/da2527tv7gurs51ef6v0';

const contactInfo = [
  { icon: 'ri-mail-fill', label: '合作与投稿', value: 'hello@banana-comics.example' },
  { icon: 'ri-chat-3-fill', label: '读者反馈', value: 'feedback@banana-comics.example' },
  { icon: 'ri-time-fill', label: '回复时间', value: '工作日 10:00 - 18:00' },
  { icon: 'ri-global-line', label: '更新频率', value: '精选书单每日更新' },
];

const subjectOptions = ['推荐一部好漫画', '合作投稿', '功能建议', '反馈问题', '其他'];

export default function Contact() {
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    // 防垃圾填充字段：若被自动填充则静默返回成功，不提交
    const honeypot = formData.get('website_alt')?.toString().trim() ?? '';
    if (honeypot) {
      setStatus('success');
      setMessage('感谢你的留言，我们会尽快与你联系。');
      return;
    }

    setStatus('loading');
    try {
      const payload = new FormData(form);
      if (payload.has('website_alt')) payload.delete('website_alt');
      const res = await fetch(FORM_URL, { method: 'POST', body: payload });
      const responseText = await res.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const failed =
        !res.ok ||
        !parsed ||
        parsed.code !== 'OK' ||
        serverMsg.toLowerCase().includes('spam');
      if (failed) {
        setStatus('error');
        setMessage(serverMsg || '提交失败，请稍后重试。');
        return;
      }
      setStatus('success');
      setMessage(parsed?.meta?.message || '感谢你的留言，我们会尽快与你联系。');
      form.reset();
    } catch {
      setStatus('error');
      setMessage('网络异常，请稍后重试。');
    }
  }

  return (
    <section id="contact" className="w-full bg-background-950 py-16 md:py-24">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-2">
          <div>
            <span className="mb-3 inline-flex items-center gap-1.5 text-sm font-medium text-accent-400">
              <i className="ri-send-plane-fill" />
              CONTACT
            </span>
            <h2 className="font-heading text-2xl font-bold text-foreground-50 md:text-4xl">
              联系我们 / 安利好漫画
            </h2>
            <p className="mt-4 max-w-md text-sm leading-relaxed text-foreground-400 md:text-base">
              有想推荐给编辑的宝藏漫画，或是关于网站的任何建议？欢迎留言，编辑部会认真阅读每一条来信。
            </p>

            <div className="mt-8 flex flex-col gap-3">
              {contactInfo.map((item) => (
                <div
                  key={item.label}
                  className="flex items-center gap-4 rounded-xl border border-background-800 bg-background-900/60 px-5 py-4"
                >
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-secondary-500/15 text-xl text-secondary-300">
                    <i className={item.icon} />
                  </span>
                  <div className="flex flex-col gap-0.5">
                    <span className="text-xs text-foreground-500">{item.label}</span>
                    <span className="text-sm font-medium text-foreground-100">{item.value}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-2xl border border-background-800 bg-background-900/60 p-6 md:p-8">
            <form id="contactForm" data-readdy-form="香蕉漫画社联系我们" onSubmit={handleSubmit} className="flex flex-col gap-4">
              {/* 防垃圾填充字段 */}
              <input
                type="text"
                name="website_alt"
                data-hp
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="flex flex-col gap-1.5">
                  <label htmlFor="name" className="text-sm font-medium text-foreground-200">
                    你的昵称
                  </label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    required
                    placeholder="怎么称呼你"
                    className="w-full rounded-md border border-background-700 bg-background-950 px-4 py-3 text-sm text-foreground-100 placeholder:text-foreground-500 focus:border-primary-500 focus:outline-none"
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label htmlFor="email" className="text-sm font-medium text-foreground-200">
                    联系邮箱
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    placeholder="you@example.com"
                    className="w-full rounded-md border border-background-700 bg-background-950 px-4 py-3 text-sm text-foreground-100 placeholder:text-foreground-500 focus:border-primary-500 focus:outline-none"
                  />
                </div>
              </div>

              <div className="flex flex-col gap-1.5">
                <label htmlFor="subject" className="text-sm font-medium text-foreground-200">
                  留言类型
                </label>
                <select
                  id="subject"
                  name="subject"
                  required
                  defaultValue={subjectOptions[0]}
                  className="w-full rounded-md border border-background-700 bg-background-950 px-4 py-3 text-sm text-foreground-100 focus:border-primary-500 focus:outline-none"
                >
                  {subjectOptions.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex flex-col gap-1.5">
                <label htmlFor="message" className="text-sm font-medium text-foreground-200">
                  留言内容
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  rows={5}
                  maxLength={500}
                  placeholder="想推荐的漫画 / 想说的话（500 字以内）"
                  className="w-full resize-none rounded-md border border-background-700 bg-background-950 px-4 py-3 text-sm text-foreground-100 placeholder:text-foreground-500 focus:border-primary-500 focus:outline-none"
                />
                <span className="text-right text-xs text-foreground-500">最多 500 字</span>
              </div>

              {status === 'success' && (
                <div className="flex items-center gap-2 rounded-md border border-secondary-500/40 bg-secondary-500/10 px-4 py-3 text-sm text-secondary-300">
                  <i className="ri-checkbox-circle-fill text-base" />
                  {message}
                </div>
              )}
              {status === 'error' && (
                <div className="flex items-start gap-2 rounded-md border border-accent-500/40 bg-accent-500/10 px-4 py-3 text-sm text-accent-300">
                  <i className="ri-error-warning-fill mt-0.5 text-base" />
                  {message}
                </div>
              )}

              <button
                type="submit"
                disabled={status === 'loading'}
                className="mt-1 inline-flex w-full items-center justify-center whitespace-nowrap rounded-lg bg-primary-500 px-6 py-3.5 text-sm font-bold text-foreground-950 transition-colors hover:bg-primary-400 disabled:opacity-60"
              >
                {status === 'loading' ? (
                  <>
                    <i className="ri-loader-4-line mr-2 animate-spin text-base" />
                    提交中...
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-fill mr-2 text-base" />
                    提交留言
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}